/**
 * Stock on-hand browser — `/inventory/stock`.
 *
 * Server-driven keyset list over `GET /stock-levels`. Quantities and values
 * are exact decimal strings end to end; nothing here parses one into a JS
 * number. Row actions hand off to the movement ledger (drill-in), the
 * movement entry form and the per-variant cover statistics.
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ColumnDef } from "@tanstack/react-table";
import {
  Button,
  Checkbox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@erp/ui";

import { DataTable, dateTimeCell, textCell } from "@/components/data-table";

import type { StockLevel } from "../api";
import { useStockLevels, useWarehouseOptions, type StockLevelFilters } from "../queries";
import { AmountText, QuantityText, RatioText } from "../components/decimal-text";
import { ALL, CategorySelect, LocationSelect } from "../components/pickers";
import { ScanBox } from "../components/scan-box";

export interface StockPageProps {
  initialSku?: string;
  initialVariantId?: string;
  initialWarehouseId?: string;
  initialLocationId?: string;
  canReadCategories?: boolean;
  canScan?: boolean;
  canReadMovements?: boolean;
  canCreateMovement?: boolean;
  canReadReports?: boolean;
  onOpenMovements?: (filters: { variantId: string; warehouseId: string }) => void;
  onNewMovement?: (prefill: { variantId: string; warehouseId: string }) => void;
  onOpenVariantStats?: (variantId: string) => void;
  pageLimit?: number;
}

export function StockPage({
  initialSku,
  initialVariantId,
  initialWarehouseId,
  initialLocationId,
  canReadCategories = false,
  canScan = false,
  canReadMovements = false,
  canCreateMovement = false,
  canReadReports = false,
  onOpenMovements,
  onNewMovement,
  onOpenVariantStats,
  pageLimit,
}: StockPageProps) {
  const { t } = useTranslation();

  const [sku, setSku] = useState(initialSku ?? "");
  const [variantId, setVariantId] = useState<string | null>(initialVariantId ?? null);
  const [warehouseId, setWarehouseId] = useState<string | null>(initialWarehouseId ?? null);
  const [locationId, setLocationId] = useState<string | null>(initialLocationId ?? null);
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [nonzero, setNonzero] = useState(true);
  const [availableOnly, setAvailableOnly] = useState(false);

  useEffect(() => {
    if (initialWarehouseId !== undefined) setWarehouseId(initialWarehouseId);
    if (initialLocationId !== undefined) setLocationId(initialLocationId);
    if (initialVariantId !== undefined) setVariantId(initialVariantId);
    if (initialSku !== undefined) setSku(initialSku);
  }, [initialWarehouseId, initialLocationId, initialVariantId, initialSku]);

  const warehousesQuery = useWarehouseOptions();
  const warehouses = warehousesQuery.data ?? [];

  const filters = useMemo<StockLevelFilters>(() => {
    const built: StockLevelFilters = {};
    if (sku !== "") built.sku = sku;
    if (variantId !== null) built.variant_id = variantId;
    if (warehouseId !== null) built.warehouse_id = warehouseId;
    if (locationId !== null) built.location_id = locationId;
    if (categoryId !== null) built.category_id = categoryId;
    if (nonzero) built.nonzero = true;
    if (availableOnly) built.available_only = true;
    return built;
  }, [sku, variantId, warehouseId, locationId, categoryId, nonzero, availableOnly]);

  const hasFilters =
    sku !== "" ||
    variantId !== null ||
    warehouseId !== null ||
    locationId !== null ||
    categoryId !== null ||
    availableOnly;

  const query = useStockLevels(filters, pageLimit);

  const clearFilters = () => {
    setSku("");
    setVariantId(null);
    setWarehouseId(null);
    setLocationId(null);
    setCategoryId(null);
    setAvailableOnly(false);
  };

  const columns = useMemo<ColumnDef<StockLevel>[]>(
    () => [
      {
        accessorKey: "sku",
        id: "sku",
        header: t("inventory.stock.columns.sku", "SKU"),
        enableSorting: false,
        cell: (ctx) => (
          <span dir="ltr" className="font-mono font-medium">
            {ctx.getValue<string>()}
          </span>
        ),
      },
      {
        accessorKey: "warehouse_code",
        id: "warehouse",
        header: t("inventory.common.warehouse", "Warehouse"),
        enableSorting: false,
        cell: (ctx) => textCell(ctx.getValue<string>()),
      },
      {
        accessorKey: "location_code",
        id: "location",
        header: t("inventory.common.location", "Bin"),
        enableSorting: false,
        cell: (ctx) => textCell(ctx.getValue<string>()),
      },
      {
        accessorKey: "batch_no",
        id: "batch",
        header: t("inventory.common.batch", "Batch"),
        enableSorting: false,
        cell: (ctx) => textCell(ctx.getValue<string | undefined>()),
      },
      {
        accessorKey: "qty",
        id: "qty",
        header: t("inventory.stock.columns.onHand", "On hand"),
        enableSorting: false,
        meta: { numeric: true },
        cell: (ctx) => <QuantityText value={ctx.getValue<string>()} />,
      },
      {
        accessorKey: "reserved",
        id: "reserved",
        header: t("inventory.stock.columns.reserved", "Reserved"),
        enableSorting: false,
        meta: { numeric: true },
        cell: (ctx) => <QuantityText value={ctx.getValue<string>()} />,
      },
      {
        accessorKey: "available_qty",
        id: "available_qty",
        header: t("inventory.stock.columns.available", "Available"),
        enableSorting: false,
        meta: { numeric: true },
        cell: (ctx) => <QuantityText value={ctx.getValue<string>()} />,
      },
      {
        accessorKey: "value",
        id: "value",
        header: t("inventory.stock.columns.value", "Value"),
        enableSorting: false,
        meta: { numeric: true },
        cell: (ctx) => <AmountText value={ctx.getValue<string>()} />,
      },
      {
        accessorKey: "average_unit_value",
        id: "average_unit_value",
        header: t("inventory.stock.columns.avgUnitValue", "Avg unit value"),
        enableSorting: false,
        meta: { numeric: true },
        cell: (ctx) => {
          const value = ctx.getValue<string | undefined>();
          // Omitted by the contract exactly when qty is zero.
          return value === undefined ? textCell(undefined) : <RatioText value={value} />;
        },
      },
      {
        accessorKey: "updated_at",
        id: "updated_at",
        header: t("inventory.stock.columns.updated", "Updated"),
        enableSorting: false,
        cell: (ctx) => dateTimeCell(ctx.getValue<string>()),
      },
      {
        id: "actions",
        header: t("inventory.common.actions", "Actions"),
        enableSorting: false,
        meta: { align: "end" },
        cell: (ctx) => {
          const row = ctx.row.original;
          return (
            <span className="flex flex-wrap justify-end gap-1">
              {canReadMovements && onOpenMovements !== undefined ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={(event) => {
                    event.stopPropagation();
                    onOpenMovements({ variantId: row.variant_id, warehouseId: row.warehouse_id });
                  }}
                >
                  {t("inventory.stock.history", "History")}
                </Button>
              ) : null}
              {canCreateMovement && onNewMovement !== undefined ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={(event) => {
                    event.stopPropagation();
                    onNewMovement({ variantId: row.variant_id, warehouseId: row.warehouse_id });
                  }}
                >
                  {t("inventory.stock.newMovement", "New movement")}
                </Button>
              ) : null}
              {canReadReports && onOpenVariantStats !== undefined ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={(event) => {
                    event.stopPropagation();
                    onOpenVariantStats(row.variant_id);
                  }}
                >
                  {t("inventory.stock.cover", "Cover")}
                </Button>
              ) : null}
            </span>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- action cells derive from the permission props above
    [t, canReadMovements, canCreateMovement, canReadReports],
  );

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">{t("inventory.stock.title", "Stock on hand")}</h1>
          <p className="text-sm text-muted-foreground">
            {t(
              "inventory.stock.subtitle",
              "Positions by SKU, warehouse and bin, valued at the locked moving average.",
            )}
          </p>
        </div>
        <ScanBox
          id="stock-scan"
          className="min-w-64"
          canResolve={canScan}
          label={t("inventory.stock.scan", "Scan to filter")}
          onResolved={(variant) => {
            setVariantId(variant.id);
            setSku("");
          }}
        />
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="stock-sku">{t("inventory.stock.columns.sku", "SKU")}</Label>
          <Input
            id="stock-sku"
            dir="ltr"
            value={sku}
            onChange={(event) => {
              setSku(event.target.value);
              if (event.target.value !== "") setVariantId(null);
            }}
            placeholder={t("inventory.stock.skuPlaceholder", "Exact SKU…")}
          />
          <p className="text-xs text-muted-foreground">
            {t("inventory.stock.skuHint", "Case-insensitive exact match — never fuzzy.")}
          </p>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="stock-warehouse">{t("inventory.common.warehouse", "Warehouse")}</Label>
          <Select
            value={warehouseId ?? ALL}
            onValueChange={(value) => {
              setWarehouseId(value === ALL ? null : value);
              setLocationId(null);
            }}
          >
            <SelectTrigger id="stock-warehouse">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t("inventory.common.all", "All")}</SelectItem>
              {warehouses.map((warehouse) => (
                <SelectItem key={warehouse.id} value={warehouse.id}>
                  {warehouse.code} — {warehouse.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {warehouseId !== null ? (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="stock-location">{t("inventory.common.location", "Bin")}</Label>
            <LocationSelect
              id="stock-location"
              warehouseId={warehouseId}
              value={locationId}
              includeAll
              onChange={setLocationId}
            />
          </div>
        ) : null}
        {canReadCategories ? (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="stock-category">{t("inventory.common.category", "Category")}</Label>
            <CategorySelect
              id="stock-category"
              value={categoryId}
              canRead={canReadCategories}
              onChange={setCategoryId}
            />
          </div>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <Checkbox
            id="stock-nonzero"
            checked={nonzero}
            onCheckedChange={(checked) => setNonzero(checked === true)}
          />
          <Label htmlFor="stock-nonzero">{t("inventory.stock.nonzero", "Non-zero only")}</Label>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            id="stock-available"
            checked={availableOnly}
            onCheckedChange={(checked) => setAvailableOnly(checked === true)}
          />
          <Label htmlFor="stock-available">
            {t("inventory.stock.availableOnly", "Available (unreserved) only")}
          </Label>
        </div>
        {hasFilters ? (
          <Button
            type="button"
            variant="link"
            size="sm"
            className="h-auto p-0 text-xs"
            onClick={clearFilters}
          >
            {t("inventory.common.clearFilters", "Clear filters")}
          </Button>
        ) : null}
      </div>

      <DataTable
        columns={columns}
        query={query}
        getRowId={(row) => row.id}
        storageKey="inventory.stock.list.columns"
        emptyMessage={
          hasFilters
            ? t("inventory.stock.emptyFiltered", "No stock matches. Clear a filter and try again.")
            : t("inventory.stock.empty", "No stock recorded yet.")
        }
        errorMessage={t("inventory.stock.loadError", "Stock could not be loaded.")}
      />
    </div>
  );
}
